import { AxiosInstance } from 'axios';
import { ErrorInterpretationDto, ErrorRelationState } from './api';
import { day } from './trace';

/**
 * What went wrong in one environment on one day, what has become of it since, and whether
 * the customer has to hear about it. Mirrors DailyReportDto on the server.
 */

export type ReportObjectStatus =
    | 'Resolved'
    | 'WaitingOnCustomer'
    | 'WaitingOnUs'
    | 'ResolvesItself'
    | 'NeedsRequeue'
    | 'Closed';

export interface DailyReportObject {
    objectType: string;
    eventType: string;
    relationId: string;
    objectGuid?: string | null;
    customerId?: string | null;
    invoiceId?: string | null;
    attempts: number;
    firstFailedAt?: string | null;
    lastFailedAt?: string | null;
    exactState: ErrorRelationState;
    status: ReportObjectStatus;
    resolution?: string | null;
    lastMessage?: string | null;
    interpretation?: ErrorInterpretationDto | null;
}

export interface DailyReportGroup {
    code: string;
    title: string;
    interpretation?: ErrorInterpretationDto | null;
    resolved: number;
    open: number;
    requeueHelps: boolean;
    objects: DailyReportObject[];
}

export interface CustomerNotice {
    needed: boolean;
    reason: string;
    subjectNl?: string | null;
    bodyNl?: string | null;
    subjectEn?: string | null;
    bodyEn?: string | null;
}

export interface DailyReport {
    customer: string;
    environment: string;
    date: string;
    summary: string;
    totals: {
        objects: number;
        attempts: number;
        resolved: number;
        waitingOnCustomer: number;
        waitingOnUs: number;
        resolvesItself: number;
        needsRequeue: number;
    };
    groups: DailyReportGroup[];
    customerNotice: CustomerNotice;
}

export const getDailyReport = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    date: Date,
    signal?: AbortSignal
): Promise<DailyReport> => {
    const response = await axios.get(
        `/Report/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/daily?date=${day(date)}`,
        { signal }
    );

    // The generated client's axios instance hands the body back as text.
    return (
        typeof response.data === 'string'
            ? JSON.parse(response.data)
            : response.data
    ) as DailyReport;
};

export const STATUS_LABEL: Record<ReportObjectStatus, string> = {
    Resolved: 'Opgelost',
    WaitingOnCustomer: 'Wacht op klant',
    WaitingOnUs: 'Wacht op ons',
    ResolvesItself: 'Wordt nog geprobeerd',
    NeedsRequeue: 'Opnieuw aanbieden',
    Closed: 'Gesloten',
};
